import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Users } from 'lucide-react';

export type LoginRole = 'employee' | 'courier';

interface RoleSelectorProps {
  selectedRole: LoginRole;
  onRoleChange: (role: LoginRole) => void;
}

/**
 * Role Selector Component
 * 
 * Allows users to select their login type:
 * - HAAT Employee (SSO login)
 * - External Employee (username/password login)
 */
const RoleSelector: React.FC<RoleSelectorProps> = ({ selectedRole, onRoleChange }) => {
  const roles: { id: LoginRole; label: string; icon: React.ReactNode; description: string }[] = [
    {
      id: 'employee',
      label: 'HAAT Internal',
      icon: <Building2 className="w-5 h-5" />,
      description: 'Sign in with Google SSO',
    },
    {
      id: 'courier',
      label: 'HAAT External',
      icon: <Users className="w-5 h-5" />,
      description: 'Sign in with credentials',
    },
  ];

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Sign in as...
      </label>
      <div className="grid grid-cols-2 gap-3">
        {roles.map((role) => {
          const isSelected = selectedRole === role.id;
          return (
            <motion.button
              key={role.id}
              type="button"
              onClick={() => onRoleChange(role.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                relative p-4 rounded-xl border-2 transition-all duration-200
                flex flex-col items-center gap-2 text-center
                ${isSelected
                  ? 'border-primary-500 bg-gradient-to-br from-primary-500/5 to-primary-500/10 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {/* Selection indicator */}
              {isSelected && (
                <motion.div
                  layoutId="roleIndicator"
                  className="absolute top-2 right-2 w-3 h-3 rounded-full bg-primary-500"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}

              {/* Icon */}
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center
                ${isSelected
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-500'
                }
                transition-colors duration-200
              `}>
                {role.icon}
              </div>

              {/* Label */}
              <span className={`
                font-semibold text-sm
                ${isSelected ? 'text-primary-500' : 'text-gray-700'}
              `}>
                {role.label}
              </span>

              {/* Description */}
              <span className="text-xs text-gray-500">
                {role.description}
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default RoleSelector;

